<template>
  <div class="signin">
    <div class="signin__backdrop"></div>

    <v-container class="signin__content">
      <div class="signin__hero">
        <h1 class="signin__title hidden-sm-and-down">
          Face and Fingerprint Biometrics
        </h1>
        <p class="signin__tagline">"Light, Simple, Fast and Easy"</p>
      </div>

      <v-row justify="center">
        <v-card class="signin__card pa-6" width="400">
          <div class="text-center mb-6">
            <v-avatar size="56" class="signin__card-icon mb-3">
              <v-icon size="30" color="white">mdi-fingerprint</v-icon>
            </v-avatar>
            <h2 class="signin__card-title">Administrator Login</h2>
            <p class="signin__card-subtitle">
              Sign in to manage your biometric system
            </p>
          </div>
          <v-card-text class="pa-0">
            <v-text-field
              outlined
              dense
              prepend-inner-icon="mdi-account-outline"
              label="Username"
              v-model="form.username"
              autofocus
              @keyup.enter="login()"
            ></v-text-field>
            <v-text-field
              outlined
              dense
              prepend-inner-icon="mdi-lock-outline"
              :append-icon="
                show_password ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              "
              @click:append="show_password = !show_password"
              label="Password"
              :type="show_password ? 'text' : 'password'"
              v-model="form.password"
              @keyup.enter="login()"
            ></v-text-field>
          </v-card-text>
          <v-card-actions class="pa-0">
            <v-btn
              color="primary"
              block
              large
              @click="login()"
              class="signin__submit"
            >
              Sign In
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-row>

      <div class="signin__footer">
        <p class="signin__footer-line">
          Developed by: JMBComputers | Jan Michael Besinga 2023
          <br />
          ZKTECO | NGTECO BIOMETRICS
        </p>
        <p class="signin__footer-version">Version {{ packageJson.version }}</p>
      </div>

      <progress-circular :progress="progress_circular" />
    </v-container>
  </div>
</template>

<script>
import ProgressCircular from "@/components/ProgressCircular.vue";
import { mapActions, mapMutations } from "vuex";
import packageJson from "../../package.json";

export default {
  name: "SignIn",
  components: {
    ProgressCircular,
  },
  data() {
    return {
      packageJson: packageJson,
      progress_circular: false,
      show_password: false,
      form: {
        username: "",
        password: "",
      },
    };
  },
  methods: {
    ...mapActions({
      signin: "auth/signin",
      set_notification: "notification/set_notification",
    }),
    ...mapMutations({
      set_progress: "auth/SET_PROGRESS_CIRCULAR",
    }),
    async login() {
      this.set_progress(true);
      try {
        await this.signin(this.form);
        this.$router.push("/dashboard");
        this.set_notification({
          message: "Successfully logged in!",
          color: "success",
          showing: true,
          icon: "mdi-check",
        });
        this.set_progress(false);
      } catch (error) {
        this.set_progress(false);

        let message;
        if (!error.response) {
          message =
            "Cannot reach the server. Please check your connection or contact the administrator";
        } else if (error.response.status == 401) {
          message = "Incorrect username or password";
        } else {
          message = "Server error: Please contact the administrator";
        }

        this.set_notification({
          message: message,
          color: "error",
          showing: true,
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>

<style scoped>
.signin {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}
.signin__backdrop {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      135deg,
      rgba(15, 23, 42, 0.88) 0%,
      rgba(49, 46, 129, 0.82) 50%,
      rgba(15, 23, 42, 0.9) 100%
    ),
    url("~@/assets/biometric.jpg") center / cover no-repeat;
}
.signin__content {
  position: relative;
  z-index: 1;
  padding-top: 8vh;
}

.signin__hero {
  text-align: center;
  margin-bottom: 40px;
  animation: rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.signin__title {
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #ffffff;
  margin-bottom: 8px;
}
.signin__tagline {
  color: rgba(226, 232, 240, 0.75);
  font-size: 1rem;
  letter-spacing: 0.02em;
}

.signin__card {
  background: rgba(255, 255, 255, 0.92) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 24px 64px rgba(2, 6, 23, 0.45) !important;
  animation: rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
}
.signin__card-icon {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.45);
}
.signin__card-title {
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
}
.signin__card-subtitle {
  font-size: 0.82rem;
  color: #64748b;
  margin: 4px 0 0;
}
.signin__submit {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.signin__footer {
  margin-top: 48px;
  text-align: center;
  animation: rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.24s both;
}
.signin__footer-line {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(203, 213, 225, 0.7);
  margin-bottom: 4px;
}
.signin__footer-version {
  font-size: 0.72rem;
  color: rgba(148, 163, 184, 0.6);
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
